import React, { useState, useMemo, useEffect } from 'react';
import { FileItem, ViewMode, BreadcrumbItem } from './types';
import { SupabaseFileStorage } from './utils/supabaseFileStorage';
import { Sidebar } from './components/Sidebar';
import { Toolbar, ViewControls } from './components/Toolbar';
import { Breadcrumbs } from './components/Breadcrumbs';
import { FileGrid } from './components/FileGrid';
import { UploadModal } from './components/UploadModal';
import { CreateFolderModal } from './components/CreateFolderModal';
import { FolderSelectionModal } from './components/FolderSelectionModal';
import { FilePreviewModal } from './components/FilePreviewModal';
import { RenameModal } from './components/RenameModal';
import { DetailsModal } from './components/DetailsModal';
import { ShareModal } from './components/ShareModal';
import { UploadProgressPopup, UploadProgress } from './components/UploadProgressPopup';
import { useAuth } from '../../context/AuthContext';
import { useWorkspace } from '../../context/WorkspaceContext';
import { fileManagerPreloader } from './utils/fileManagerPreloader';
import { toast } from 'sonner';
import { uploadToR2 } from '../../lib/r2-storage';
import { supabase } from '../../lib/supabase';

interface FileManagerProps {
  isGlobalDarkMode: boolean;
}

export const FileManager: React.FC<FileManagerProps> = ({ isGlobalDarkMode }) => {
  const { profile, user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const [files, setFiles] = useState<FileItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentFolderId, setCurrentFolderId] = useState<string | undefined>();
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>({ type: 'grid' });
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [itemToMove, setItemToMove] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('all');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return document.body.getAttribute('data-sidebar-collapsed') === 'true' || window.innerWidth < 1024;
  });
  const [fileTypeFilter, setFileTypeFilter] = useState<string>('all');
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewFile, setPreviewFile] = useState<FileItem | null>(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [itemToRename, setItemToRename] = useState<FileItem | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [itemDetails, setItemDetails] = useState<FileItem | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareTarget, setShareTarget] = useState<FileItem | null>(null);
  const [sharedWithMeFiles, setSharedWithMeFiles] = useState<FileItem[]>([]);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  // Get current user's name
  const currentUserName = useMemo(() => {
    if (profile?.first_name || profile?.last_name) {
      const fullName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
      if (fullName) return fullName;
    }
    if (profile?.email) {
      // Use email username part as fallback
      return profile.email.split('@')[0];
    }
    return 'User';
  }, [profile]);

  // Get current user's avatar URL
  const currentUserAvatarUrl = profile?.avatar_url || null;

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (mobile && !sidebarCollapsed) {
        setSidebarCollapsed(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarCollapsed]);

  // Listen for global header secondary sidebar toggle
  useEffect(() => {
    const handleToggle = (e: any) => {
      if (typeof e.detail?.collapsed === 'boolean') {
        setSidebarCollapsed(e.detail.collapsed);
      } else {
        setSidebarCollapsed(prev => !prev);
      }
    };
    window.addEventListener('toggle-secondary-sidebar', handleToggle);
    return () => window.removeEventListener('toggle-secondary-sidebar', handleToggle);
  }, []);

  // Load files from Supabase when workspace changes (with cache optimization)
  useEffect(() => {
    const loadFiles = async () => {
      if (!currentWorkspace) return;

      setLoading(true);

      // Try to get cached data first for instant display
      const cached = fileManagerPreloader.getCached(currentWorkspace.id);
      if (cached) {
        setFiles(cached.files);
        setStorageInfo(cached.storageInfo);
        setLoading(false);

        // Refresh in background to ensure data is up-to-date
        fileManagerPreloader.preload(currentWorkspace.id).then(() => {
          const updated = fileManagerPreloader.getCached(currentWorkspace.id);
          if (updated) {
            setFiles(updated.files);
            setStorageInfo(updated.storageInfo);
          }
        }).catch(console.error);
      } else {
        // No cache, load fresh data
        const [loadedFiles, storageInfo] = await Promise.all([
          SupabaseFileStorage.getFiles(currentWorkspace.id),
          SupabaseFileStorage.getStorageInfo(currentWorkspace.id),
        ]);
        setFiles(loadedFiles);
        setStorageInfo(storageInfo);
        setLoading(false);

        // Cache the data for future use
        fileManagerPreloader.preload(currentWorkspace.id).catch(console.error);
      }
    };

    loadFiles();
  }, [currentWorkspace]);

  // Realtime subscription — refresh whenever workspace_files changes so both
  // the File Manager and Project Files tab stay in sync automatically.
  useEffect(() => {
    if (!currentWorkspace) return;

    const channel = supabase
      .channel(`workspace_files:${currentWorkspace.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'workspace_files',
          filter: `workspace_id=eq.${currentWorkspace.id}`,
        },
        () => {
          fileManagerPreloader.invalidate(currentWorkspace.id);
          SupabaseFileStorage.getFiles(currentWorkspace.id).then(updated => {
            setFiles(updated);
          }).catch(console.error);
          SupabaseFileStorage.getStorageInfo(currentWorkspace.id).then(info => {
            setStorageInfo(info);
          }).catch(console.error);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentWorkspace?.id]);

  const [storageInfo, setStorageInfo] = useState({ used: 0, total: 100 * 1024 * 1024 });

  // Load files shared with the current user when the 'shared' filter is active
  useEffect(() => {
    if (filter !== 'shared' || !currentWorkspace || !user) return;
    SupabaseFileStorage.getFilesSharedWithMe(user.id, currentWorkspace.id)
      .then(setSharedWithMeFiles)
      .catch(console.error);
  }, [filter, currentWorkspace?.id, user?.id]);

  // Storage info is now loaded together with files in the main useEffect above
  // This useEffect is kept for backward compatibility but is no longer needed

  const currentFiles = useMemo(() => {
    let filteredFiles = files;

    // Filter out trashed files from normal views
    if (filter !== 'trash') {
      filteredFiles = files.filter(file => !file.trashed);
    }

    // Apply folder filter
    if (filter === 'bookmarked') {
      filteredFiles = filteredFiles.filter(file => file.starred);
    } else if (filter === 'shared') {
      filteredFiles = sharedWithMeFiles;
    } else if (filter === 'recent') {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      filteredFiles = filteredFiles
        .filter(file => file.modifiedAt >= sevenDaysAgo)
        .sort((a, b) => b.modifiedAt.getTime() - a.modifiedAt.getTime());
    } else if (filter === 'trash') {
      filteredFiles = files.filter(file => file.trashed);
    } else {
      // Show files in current folder
      filteredFiles = filteredFiles.filter(file => file.parentId === currentFolderId);
    }

    // Apply file type filter
    if (fileTypeFilter !== 'all') {
      filteredFiles = filteredFiles.filter(file => {
        if (file.type === 'folder') return true; // Always show folders

        const mimeType = file.mimeType?.toLowerCase() || '';

        switch (fileTypeFilter) {
          case 'logos':
            return mimeType.includes('image/') && (
              file.name.toLowerCase().includes('logo') ||
              mimeType.includes('svg') ||
              mimeType.includes('ai') ||
              mimeType.includes('eps')
            );
          case 'images':
            return mimeType.includes('image/');
          case 'icons':
            return mimeType.includes('image/') && (
              file.name.toLowerCase().includes('icon') ||
              mimeType.includes('svg') ||
              file.name.toLowerCase().includes('ico')
            );
          case 'documents':
            return mimeType.includes('pdf') ||
              mimeType.includes('document') ||
              mimeType.includes('text') ||
              mimeType.includes('msword') ||
              mimeType.includes('officedocument');
          default:
            return true;
        }
      });
    }
    // Apply search filter
    if (searchQuery) {
      filteredFiles = filteredFiles.filter(file =>
        file.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return filteredFiles;
  }, [files, currentFolderId, searchQuery, filter, fileTypeFilter]);

  const breadcrumbs = useMemo(() => {
    const crumbs: BreadcrumbItem[] = [];
    let folderId = currentFolderId;

    while (folderId) {
      const folder = files.find(f => f.id === folderId);
      if (!folder) break;

      crumbs.unshift({ id: folder.id, name: folder.name });
      folderId = folder.parentId;
    }

    return crumbs;
  }, [files, currentFolderId]);

  const handleFolderSelect = (folderId?: string) => {
    // Clear selection when navigating to different folders/views
    setSelectionMode(false);
    setSelectedItems(new Set());

    if (folderId === 'bookmarked' || folderId === 'shared' || folderId === 'recent' || folderId === 'trash') {
      setFilter(folderId);
      setCurrentFolderId(undefined);
    } else {
      setFilter('all');
      setCurrentFolderId(folderId);
    }
  };

  const handleFolderOpen = (folderId: string) => {
    // Clear selection when opening folders
    setSelectionMode(false);
    setSelectedItems(new Set());
    handleFolderSelect(folderId);
  };

  const handleFileDelete = async (id: string) => {
    if (!currentWorkspace) return;
    const item = files.find(f => f.id === id);
    if (item?.isProtected || item?.isDefault || (item?.name?.toLowerCase() === 'projects' && !item?.parentId)) {
      return;
    }
    await SupabaseFileStorage.deleteFile(id);
    const updatedFiles = await SupabaseFileStorage.getFiles(currentWorkspace.id);
    setFiles(updatedFiles);
  };

  const handleFileRestore = async (id: string) => {
    if (!currentWorkspace) return;
    await SupabaseFileStorage.restoreFile(id);
    fileManagerPreloader.invalidate(currentWorkspace.id);
    const updatedFiles = await SupabaseFileStorage.getFiles(currentWorkspace.id);
    setFiles(updatedFiles);
  };

  const handlePermanentDelete = async (id: string) => {
    if (!currentWorkspace) return;
    const item = files.find(f => f.id === id);
    if (item?.isProtected || item?.isDefault || (item?.name?.toLowerCase() === 'projects' && !item?.parentId)) {
      return;
    }
    await SupabaseFileStorage.permanentlyDeleteFile(id);
    fileManagerPreloader.invalidate(currentWorkspace.id);
    const updatedFiles = await SupabaseFileStorage.getFiles(currentWorkspace.id);
    setFiles(updatedFiles);
  };

  const handleToggleStar = async (id: string) => {
    if (!currentWorkspace) return;
    const file = files.find(f => f.id === id);
    if (file) {
      await SupabaseFileStorage.updateFile(id, { starred: !file.starred, modifiedBy: currentUserName });
      fileManagerPreloader.invalidate(currentWorkspace.id);
      const updatedFiles = await SupabaseFileStorage.getFiles(currentWorkspace.id);
      setFiles(updatedFiles);
    }
  };

  const handleShare = (id: string) => {
    const item = files.find(f => f.id === id);
    if (item) {
      setShareTarget(item);
      setShowShareModal(true);
    }
  };

  const handleShareModalClose = async () => {
    setShowShareModal(false);
    setShareTarget(null);
    // Refresh file list to update shared flags
    if (currentWorkspace) {
      fileManagerPreloader.invalidate(currentWorkspace.id);
      const updatedFiles = await SupabaseFileStorage.getFiles(currentWorkspace.id);
      setFiles(updatedFiles);
    }
  };

  const handleDuplicate = async (id: string) => {
    if (!currentWorkspace || !user) return;
    const file = files.find(f => f.id === id);
    if (file) {
      const duplicatedFile = {
        ...file,
        name: `${file.name} - Copy`,
        starred: false,
        shared: false,
        ownerId: profile?.id || file.ownerId,
        ownerName: currentUserName || file.ownerName,
        ownerAvatarUrl: currentUserAvatarUrl || file.ownerAvatarUrl,
        modifiedBy: currentUserName,
      };
      delete (duplicatedFile as any).id;
      delete (duplicatedFile as any).createdAt;
      delete (duplicatedFile as any).modifiedAt;
      await SupabaseFileStorage.addFile(currentWorkspace.id, user.id, duplicatedFile);
      fileManagerPreloader.invalidate(currentWorkspace.id);
      const updatedFiles = await SupabaseFileStorage.getFiles(currentWorkspace.id);
      setFiles(updatedFiles);
    }
  };

  const handleMoveTo = (id: string) => {
    setItemToMove(id);
    setShowMoveModal(true);
  };

  const handleRename = (id: string) => {
    const item = files.find(f => f.id === id);
    if (item && !item.isProtected && !item.isDefault && !(item.name?.toLowerCase() === 'projects' && !item.parentId)) {
      setItemToRename(item);
      setShowRenameModal(true);
    }
  };

  const handleFileDetails = (file: FileItem) => {
    setItemDetails(file);
    setShowDetailsModal(true);
  };

  const handleFolderDetails = (folder: FileItem) => {
    setItemDetails(folder);
    setShowDetailsModal(true);
  };

  const handleRenameConfirm = async (newName: string) => {
    if (!currentWorkspace || !itemToRename || !newName.trim()) return;
    if (itemToRename.isProtected || itemToRename.isDefault || (itemToRename.name?.toLowerCase() === 'projects' && !itemToRename.parentId)) {
      return;
    }
    await SupabaseFileStorage.updateFile(itemToRename.id, { name: newName.trim(), modifiedBy: currentUserName });
    fileManagerPreloader.invalidate(currentWorkspace.id);
    const updatedFiles = await SupabaseFileStorage.getFiles(currentWorkspace.id);
    setFiles(updatedFiles);
    setItemToRename(null);
  };

  const handleMoveConfirm = async (targetFolderId?: string) => {
    if (!currentWorkspace || !itemToMove) return;
    await SupabaseFileStorage.updateFile(itemToMove, { parentId: targetFolderId, modifiedBy: currentUserName });
    fileManagerPreloader.invalidate(currentWorkspace.id);
    const updatedFiles = await SupabaseFileStorage.getFiles(currentWorkspace.id);
    setFiles(updatedFiles);

    setItemToMove(null);
    setSelectedItems(new Set()); // Clear selection after move
  };

  const handleUpload = async (uploadedFiles: File[]) => {
    if (!currentWorkspace || !user) return;

    // Calculate total size of files to upload
    const totalUploadSize = uploadedFiles.reduce((sum, file) => sum + file.size, 0);

    // Check if upload would exceed storage limit
    const newTotalUsed = storageInfo.used + totalUploadSize;
    if (newTotalUsed > storageInfo.total) {
      const availableSpace = storageInfo.total - storageInfo.used;
      const availableSpaceMB = (availableSpace / (1024 * 1024)).toFixed(2);
      const requiredSpaceMB = (totalUploadSize / (1024 * 1024)).toFixed(2);
      toast.error(`Storage limit exceeded. Available: ${availableSpaceMB} MB, Required: ${requiredSpaceMB} MB`);
      return;
    }

    // Initialize progress tracking for all files
    const initialProgress: UploadProgress[] = uploadedFiles.map((file, index) => ({
      id: `${Date.now()}-${index}`,
      fileName: file.name,
      progress: 0,
      status: 'pending' as const,
    }));
    setUploadProgress(initialProgress);

    // Store abort controllers for each upload
    const abortControllers = new Map<string, AbortController>();

    const uploadPromises: Promise<void>[] = [];

    for (let i = 0; i < uploadedFiles.length; i++) {
      const file = uploadedFiles[i];
      const progressId = initialProgress[i].id;
      const abortController = new AbortController();
      abortControllers.set(progressId, abortController);

      const promise = (async () => {
        try {
          // Update status to uploading
          setUploadProgress(prev =>
            prev.map(p => p.id === progressId ? { ...p, status: 'uploading' as const, progress: 10 } : p)
          );

          let publicUrl: string | undefined = undefined;

          if (file.type) {
            // Simulate progress during upload
            setUploadProgress(prev =>
              prev.map(p => p.id === progressId ? { ...p, progress: 30 } : p)
            );

            // Upload to R2 for persistent storage
            const uploadResponse = await uploadToR2({
              fileName: file.name,
              file,
              folder: `workspace_files/${currentWorkspace.id}`
            });
            publicUrl = uploadResponse.publicUrl;

            // Check if upload was cancelled
            if (abortController.signal.aborted) {
              throw new Error('Upload cancelled');
            }

            setUploadProgress(prev =>
              prev.map(p => p.id === progressId ? { ...p, progress: 70 } : p)
            );
          }

          // Check if upload was cancelled before saving to database
          if (abortController.signal.aborted) {
            throw new Error('Upload cancelled');
          }

          await SupabaseFileStorage.addFile(currentWorkspace.id, user.id, {
            name: file.name,
            type: 'file',
            size: file.size,
            mimeType: file.type,
            parentId: currentFolderId,
            starred: false,
            shared: false,
            url: publicUrl,
            trashed: false,
            ownerName: currentUserName,
            ownerAvatarUrl: currentUserAvatarUrl || undefined,
            modifiedBy: currentUserName,
          });

          // Mark as complete
          setUploadProgress(prev =>
            prev.map(p => p.id === progressId ? { ...p, status: 'success' as const, progress: 100 } : p)
          );

          abortControllers.delete(progressId);
        } catch (error) {
          if (error instanceof Error && error.message === 'Upload cancelled') {
            // Remove cancelled upload from progress list
            setUploadProgress(prev => prev.filter(p => p.id !== progressId));
          } else {
            console.error('Error processing upload:', file.name, error);
            // Mark as failed - error is shown in the upload progress popup
            setUploadProgress(prev =>
              prev.map(p => p.id === progressId ? {
                ...p,
                status: 'error' as const,
                progress: 0,
                error: 'Upload failed'
              } : p)
            );
          }
          abortControllers.delete(progressId);
        }
      })();
      uploadPromises.push(promise);
    }

    // Store abort controllers in component state so handleCancelUpload can access them
    (window as any).__uploadAbortControllers = abortControllers;

    // Wait for all uploads to complete
    await Promise.all(uploadPromises);

    // Clean up
    delete (window as any).__uploadAbortControllers;

    // Invalidate cache and reload files once after all uploads
    fileManagerPreloader.invalidate(currentWorkspace.id);
    const updatedFiles = await SupabaseFileStorage.getFiles(currentWorkspace.id);
    setFiles(updatedFiles);
  };

  const handleCancelUpload = (uploadId: string) => {
    const abortControllers = (window as any).__uploadAbortControllers as Map<string, AbortController>;
    if (abortControllers) {
      const controller = abortControllers.get(uploadId);
      if (controller) {
        controller.abort();
      }
    }
  };

  const handleCreateFolder = async (name: string) => {
    if (!currentWorkspace || !user) return;
    await SupabaseFileStorage.createFolder(currentWorkspace.id, user.id, name, currentFolderId, currentUserName, currentUserAvatarUrl);
    fileManagerPreloader.invalidate(currentWorkspace.id);
    const updatedFiles = await SupabaseFileStorage.getFiles(currentWorkspace.id);
    setFiles(updatedFiles);
  };

  const handleToggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // Drag and drop handlers for the entire page
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types.includes('Files')) {
      setIsDraggingOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only hide overlay if we're leaving the main drop zone
    if (e.currentTarget === e.target) {
      setIsDraggingOver(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);

    const items = Array.from(e.dataTransfer.items);
    const files: File[] = [];

    // Process dropped items
    for (const item of items) {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          files.push(file);
        }
      }
    }

    if (files.length > 0) {
      await handleUpload(files);
    }
  };

  const handleItemSelect = (id: string, selected: boolean) => {
    if (!selectionMode) {
      setSelectionMode(true);
    }

    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectionMode) {
      const allIds = currentFiles
        .filter(file => !file.isProtected && !file.isDefault && !(file.name?.toLowerCase() === 'projects' && !file.parentId))
        .map(file => file.id);
      setSelectedItems(new Set(allIds));
    } else {
      // Enter selection mode
      setSelectionMode(true);
    }
  };

  const handleDeselectAll = () => {
    // Exit selection mode and clear selections
    setSelectionMode(false);
    setSelectedItems(new Set());
  };

  const handleBulkDelete = async () => {
    if (!currentWorkspace) return;
    const itemsToDelete = Array.from(selectedItems).filter(id => {
      const item = files.find(f => f.id === id);
      return !item?.isProtected && !item?.isDefault && !(item?.name?.toLowerCase() === 'projects' && !item?.parentId);
    });
    for (const id of itemsToDelete) {
      await SupabaseFileStorage.deleteFile(id);
    }
    const updatedFiles = await SupabaseFileStorage.getFiles(currentWorkspace.id);
    setFiles(updatedFiles);
    setSelectedItems(new Set());
  };

  const handleBulkRestore = async () => {
    if (!currentWorkspace) return;
    for (const id of selectedItems) {
      await SupabaseFileStorage.restoreFile(id);
    }
    const updatedFiles = await SupabaseFileStorage.getFiles(currentWorkspace.id);
    setFiles(updatedFiles);
    setSelectedItems(new Set());
  };

  const handleBulkMoveTo = () => {
    if (selectedItems.size > 0) {
      const selectedIds = Array.from(selectedItems).join(',');
      setItemToMove(selectedIds);
      setShowMoveModal(true);
    }
  };

  const handleBulkMoveConfirm = async (targetFolderId?: string) => {
    if (!currentWorkspace || !itemToMove) return;
    const itemIds = itemToMove.includes(',')
      ? itemToMove.split(',').filter(id => id.trim() !== '')
      : [itemToMove];

    for (const id of itemIds) {
      await SupabaseFileStorage.updateFile(id, { parentId: targetFolderId, modifiedBy: currentUserName });
    }

    const updatedFiles = await SupabaseFileStorage.getFiles(currentWorkspace.id);
    setFiles(updatedFiles);
    setItemToMove(null);
    setSelectedItems(new Set()); // Clear selection after bulk move
  };

  const handleFilePreview = (file: FileItem) => {
    setPreviewFile(file);
    setShowPreviewModal(true);
  };

  if (!currentWorkspace || loading) {
    return null;
  }

  return (
    <div className={`h-screen w-full ${isGlobalDarkMode ? 'bg-background' : 'bg-transparent'}`}>
      {/* Mobile Overlay Backdrop */}
      {isMobile && !sidebarCollapsed && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setSidebarCollapsed(true)}
        />
      )}

      {/* Fixed Sidebar - positioned like secondary menu sidebar on desktop, overlay on mobile */}
      {!sidebarCollapsed && (
        <div className={isMobile ? 'fixed inset-0 z-50' : ''}>
          <Sidebar
            files={files}
            currentFolderId={filter === 'all' ? currentFolderId : undefined}
            onFolderSelect={handleFolderSelect}
            storageInfo={storageInfo}
            currentFilter={filter}
            isGlobalDarkMode={isGlobalDarkMode}
            onCreateFolder={() => setShowCreateFolderModal(true)}
            isLoading={loading}
            isMobile={isMobile}
            onClose={() => setSidebarCollapsed(true)}
          />
        </div>
      )}

      {/* Main Content Area - positioned to start after sidebar */}
      <div
        className="flex flex-col min-h-0 h-full ml-0 relative"
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {/* Drag and Drop Overlay */}
        {isDraggingOver && (
          <div className="absolute inset-0 z-50 bg-workspace-accent/10 backdrop-blur-sm border-4 border-dashed border-workspace-accent flex items-center justify-center">
            <div className="bg-card/95 rounded-2xl shadow-2xl p-12 text-center border-2 border-workspace-accent">
              <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-workspace-accent/20 flex items-center justify-center">
                <svg
                  className="w-12 h-12 text-workspace-accent"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-foreground mb-2">Drop files here</h3>
              <p className="text-muted-foreground text-lg">Release to upload to {filter === 'all' ? (currentFolderId ? 'current folder' : 'My Drive') : filter}</p>
            </div>
          </div>
        )}

        <Toolbar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          onUpload={() => setShowUploadModal(true)}
          onCreateFolder={() => setShowCreateFolderModal(true)}
          onToggleSidebar={handleToggleSidebar}
          fileTypeFilter={fileTypeFilter}
          onFileTypeFilterChange={setFileTypeFilter}
          selectionMode={selectionMode}
          selectedItemsCount={selectedItems.size}
          onSelectAll={handleSelectAll}
          onDeselectAll={handleDeselectAll}
          isGlobalDarkMode={isGlobalDarkMode}
          breadcrumbSlot={
            filter === 'all' ? (
              <Breadcrumbs
                items={breadcrumbs}
                onNavigate={handleFolderSelect}
                isGlobalDarkMode={isGlobalDarkMode}
              />
            ) : undefined
          }
        />

        {/* Mobile View Controls Row - Shows only on mobile after breadcrumbs */}
        <div className="lg:hidden py-3 border-b border-border">
          <ViewControls
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            fileTypeFilter={fileTypeFilter}
            onFileTypeFilterChange={setFileTypeFilter}
            selectionMode={selectionMode}
            onSelectAll={handleSelectAll}
            onDeselectAll={handleDeselectAll}
            isGlobalDarkMode={isGlobalDarkMode}
          />
        </div>

        <FileGrid
          files={currentFiles}
          viewMode={viewMode}
          isTrashView={filter === 'trash'}
          selectionMode={selectionMode}
          selectedItems={selectedItems}
          onItemSelect={handleItemSelect}
          onFolderOpen={handleFolderOpen}
          onFileDelete={handleFileDelete}
          onFileRestore={handleFileRestore}
          onPermanentDelete={handlePermanentDelete}
          onToggleStar={handleToggleStar}
          onShare={handleShare}
          onDuplicate={handleDuplicate}
          onMoveTo={handleMoveTo}
          onRename={handleRename}
          onFilePreview={handleFilePreview}
          onFileDetails={handleFileDetails}
          onFolderDetails={handleFolderDetails}
          isGlobalDarkMode={isGlobalDarkMode}
          currentUserName={currentUserName}
          currentUserAvatarUrl={currentUserAvatarUrl}
          currentUserId={profile?.id}
          isLoading={loading}
        />
      </div>

      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUpload={handleUpload}
      />

      <CreateFolderModal
        isOpen={showCreateFolderModal}
        onClose={() => setShowCreateFolderModal(false)}
        onCreate={handleCreateFolder}
        isGlobalDarkMode={isGlobalDarkMode}
      />

      <FolderSelectionModal
        isOpen={showMoveModal}
        onClose={() => {
          setShowMoveModal(false);
          setItemToMove(null);
        }}
        onSelect={itemToMove?.includes(',') ? handleBulkMoveConfirm : handleMoveConfirm}
        files={files}
        currentItemId={itemToMove || ''}
        title={itemToMove?.includes(',') ? `Move ${itemToMove.split(',').length} items to Folder` : "Move to Folder"}
      />

      <RenameModal
        isOpen={showRenameModal}
        onClose={() => {
          setShowRenameModal(false);
          setItemToRename(null);
        }}
        onRename={handleRenameConfirm}
        currentName={itemToRename?.name || ''}
        itemType={itemToRename?.type || 'file'}
        isGlobalDarkMode={isGlobalDarkMode}
      />

      <FilePreviewModal
        isOpen={showPreviewModal}
        onClose={() => {
          setShowPreviewModal(false);
          setPreviewFile(null);
        }}
        file={previewFile}
        onToggleStar={handleToggleStar}
        onShare={handleShare}
        isGlobalDarkMode={isGlobalDarkMode}
      />

      <DetailsModal
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setItemDetails(null);
        }}
        item={itemDetails}
        isGlobalDarkMode={isGlobalDarkMode}
        currentUserName={currentUserName}
      />

      <ShareModal
        isOpen={showShareModal}
        onClose={handleShareModalClose}
        file={shareTarget}
        workspaceId={currentWorkspace?.id || ''}
        currentUserId={user?.id || ''}
      />

      {selectedItems.size > 0 && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-card rounded-lg shadow-lg border border-border px-6 py-4 flex items-center gap-4 z-50">
          <span className="text-sm font-medium text-foreground">
            {selectedItems.size} item{selectedItems.size > 1 ? 's' : ''} selected
          </span>

          <div className="flex items-center gap-2">
            {filter === 'trash' ? (
              <>
                <button
                  onClick={handleBulkRestore}
                  className="px-3 py-1.5 bg-workspace-accent hover:bg-workspace-accent-hover text-white text-sm rounded-md transition-colors duration-200"
                >
                  Restore
                </button>
                <button
                  onClick={() => {
                    selectedItems.forEach(id => handlePermanentDelete(id));
                    setSelectedItems(new Set());
                  }}
                  className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded-md transition-colors duration-200"
                >
                  Delete Permanently
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleBulkMoveTo}
                  className="px-3 py-1.5 bg-workspace-accent hover:bg-workspace-accent-hover text-white text-sm rounded-md transition-colors duration-200"
                >
                  Move To
                </button>
                <button
                  onClick={handleBulkDelete}
                  className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded-md transition-colors duration-200"
                >
                  Move to Trash
                </button>
              </>
            )}

            <button
              onClick={handleDeselectAll}
              className="px-3 py-1.5 bg-muted hover:bg-accent text-foreground text-sm rounded-md transition-colors duration-200"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Upload Progress Popup */}
      {uploadProgress.length > 0 && (
        <UploadProgressPopup
          uploads={uploadProgress}
          onClose={() => setUploadProgress([])}
          onCancel={handleCancelUpload}
        />
      )}
    </div>
  );
};
